import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

export interface HeroSlide {
  id: string;
  title: string;
  summary: string;
  image: string;
  link: string;
  tag: string;
}

export default function HeroCarousel({ slides }: { slides: HeroSlide[] }) {
  const [index, setIndex] = useState(0);
  const timer = useRef<number | null>(null);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    timer.current = window.setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, 5000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [slides.length]);

  const stopAuto = () => {
    if (timer.current) {
      window.clearInterval(timer.current);
      timer.current = null;
    }
  };

  const goTo = (i: number) => {
    setIndex((i + slides.length) % slides.length);
  };

  const handlePrev = () => {
    stopAuto();
    goTo(index - 1);
  };
  const handleNext = () => {
    stopAuto();
    goTo(index + 1);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(diff) > 40) {
      stopAuto();
      goTo(diff > 0 ? index - 1 : index + 1);
    }
    touchStartX.current = null;
  };

  const active = slides[index];
  if (!active) return null;

  return (
    <section
      className="relative h-[440px] w-full overflow-hidden md:h-[560px]"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {slides.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={s.image}
            alt={`${s.title} - 91.con焦点图`}
            title={`${s.title} - 91.con娱乐资讯`}
            className="h-full w-full object-cover"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-background-50/20"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-background-50/85 via-background-50/30 to-transparent"></div>

      <div className="absolute inset-0 flex items-center">
        <div className="mx-auto w-full px-4 md:px-6">
          <div className="max-w-2xl">
            <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary-400/50 bg-primary-500/15 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-primary-300">
              <i className="ri-fire-fill"></i>
              {active.tag}
            </span>
            <h1 className="font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-5xl">
              {active.title}
            </h1>
            <p className="mt-4 line-clamp-2 max-w-xl text-sm text-foreground-700 md:text-base">
              {active.summary}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={active.link}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-play-circle-line"></i>
                立即查看
              </Link>
              <Link
                to="/videos"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-foreground-800/20 bg-background-100/60 px-5 py-2.5 text-sm font-medium text-foreground-900 transition-colors hover:border-accent-400 hover:text-accent-400"
              >
                <i className="ri-film-line"></i>
                更多视频
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-2">
        {slides.map((s, i) => (
          <button
            key={s.id}
            type="button"
            onClick={() => {
              stopAuto();
              goTo(i);
            }}
            aria-label={`切换到第${i + 1}张`}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? "w-8 bg-primary-500" : "w-4 bg-foreground-800/30 hover:bg-foreground-800/50"
            }`}
          ></button>
        ))}
      </div>

      <button
        type="button"
        onClick={handlePrev}
        aria-label="上一张"
        className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-background-300/50 bg-background-100/70 text-foreground-900 transition-colors hover:border-primary-400 hover:text-primary-400 md:flex"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        type="button"
        onClick={handleNext}
        aria-label="下一张"
        className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-background-300/50 bg-background-100/70 text-foreground-900 transition-colors hover:border-primary-400 hover:text-primary-400 md:flex"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>
    </section>
  );
}